import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api';
import { Card, EmptyState, ErrorBanner, Loading } from '../components/ui';
import { idiomaActual, useT } from '../i18n';

/**
 * Cuánta gente entra a las páginas públicas de las influencers.
 *
 * ── Por qué las tres cifras están siempre juntas ──────────────────────────────
 * Porque por separado engañan. Las visitas no distinguen cien personas de una que recargó cien
 * veces; los visitantes no dicen si alguien llegó a hacer algo; los clics no dicen sobre cuánta
 * gente. Juntas contestan la pregunta entera: cuántos vinieron, cuántos eran distintos, y cuántos
 * siguieron hasta una red.
 */
type Estadisticas = {
  dias: number;
  /** La influencer que el servidor filtró, para no confiar en el estado local. */
  creatorId: string | null;
  porInfluencer: {
    creatorId: string;
    publicName: string;
    slug: string;
    publicada: number;
    visitas: number;
    visitantes: number;
    clics: number;
  }[];
  porDia: { dayUtc: string; visitas: number; visitantes: number; clics: number }[];
  porOrigen: { origen: string; visitas: number }[];
  porPais: { pais: string; visitas: number }[];
  porDestino: { destino: string; clics: number }[];
  porVariante: { variante: string; visitas: number; clics: number }[];
};

const NOMBRE_DE_DESTINO: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  x: 'X',
  fanvue: 'Fanvue',
};

/**
 * @param creatorFijo Cuando viene, la pantalla es la de UNA influencer y no la general.
 *
 * ── Por qué la misma pantalla y no una copia ──────────────────────────────────
 * Porque los cortes son los mismos —por día, por origen, por país, adónde van— y el servidor ya
 * sabe filtrar por influencer: el endpoint acepta `creator` desde que existe el selector. Copiar
 * el componente para la pestaña habría dado dos pantallas que muestran lo mismo y que un día
 * dejan de coincidir.
 *
 * Lo que cambia con `creatorFijo` es lo que sobra dentro de una ficha: el título general, el
 * selector de influencer —ya está elegida— y la tabla que las compara entre sí.
 */
export function Paginas({ creatorFijo }: { creatorFijo?: string } = {}) {
  const { t } = useT();
  const [dias, setDias] = useState(30);
  /**
   * Sobre qué influencer se miran los cortes. Vacío es «todas».
   *
   * ── Por qué un selector y no repetir cada panel por influencer ────────────
   * Porque los cuatro cortes de abajo son listas: repetirlos por influencer da cuatro listas por
   * cada una, y la pantalla deja de contestar nada de un vistazo. Con dos influencers ya son ocho
   * bloques. El selector mantiene la pantalla del mismo tamaño sin importar cuántas haya.
   *
   * El filtro lo aplica el SERVIDOR y no el navegador: los cortes salen de agregados en SQL —no
   * viajan las filas sueltas— así que acá no hay nada que volver a agrupar.
   */
  const [creatorId, setCreatorId] = useState('');
  // Dentro de una ficha manda la influencer de la ficha, y el estado local no participa.
  const filtrada = creatorFijo ?? creatorId;
  const state = useApi<Estadisticas>(
    `/api/paginas/estadisticas?dias=${dias}${filtrada ? `&creator=${filtrada}` : ''}`,
    60_000,
  );

  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading label="Contando visitas…" />;

  const datos = state.data;
  const elegida = datos.porInfluencer.find((fila) => fila.creatorId === datos.creatorId);

  /*
    Con una influencer elegida, las tres cifras de arriba son las SUYAS y no la suma.

    Se toman de su fila y no se recalculan: es el mismo número que muestra la tabla de abajo, y
    dos formas de calcular lo mismo es la manera de que un día no coincidan.
  */
  const total = elegida
    ? { visitas: elegida.visitas, visitantes: elegida.visitantes, clics: elegida.clics }
    : datos.porInfluencer.reduce(
        (suma, fila) => ({
          visitas: suma.visitas + fila.visitas,
          visitantes: suma.visitantes + fila.visitantes,
          clics: suma.clics + fila.clics,
        }),
        { visitas: 0, visitantes: 0, clics: 0 },
      );

  /*
    El pico del período, para dibujar las barras contra algo.

    Sin un máximo, una barra proporcional al total haría que un día flojo y uno bueno se vieran
    casi iguales cuando el total es grande. Contra el pico, la forma del período se lee de un
    vistazo.
  */
  const pico = Math.max(1, ...datos.porDia.map((dia) => dia.visitas));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Dentro de una ficha el título sobra: la pestaña ya dice dónde está uno. */}
        {creatorFijo ? (
          <div />
        ) : (
          <div>
            <h1 className="text-xl font-semibold">{t('paginas.titulo')}</h1>
            <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">
              {t('paginas.bajada', { dominio: '/p/…' })}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {/*
            De quién, antes que de cuándo.

            El período es un ajuste fino sobre una respuesta que ya se está mirando; de quién son
            estos números es la pregunta previa. Va primero por eso, no por espacio.
          */}
          {!creatorFijo && datos.porInfluencer.length > 1 && (
            <select
              value={creatorId}
              onChange={(event) => setCreatorId(event.target.value)}
              aria-label={t('paginas.porInfluencer')}
              className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
            >
              <option value="">{t('paginas.todasLasInfluencers')}</option>
              {datos.porInfluencer.map((fila) => (
                <option key={fila.creatorId} value={fila.creatorId}>
                  {fila.publicName}
                </option>
              ))}
            </select>
          )}
          {[7, 30, 90].map((opcion) => (
            <button
              key={opcion}
              type="button"
              onClick={() => setDias(opcion)}
              aria-pressed={dias === opcion}
              className={
                'rounded-lg border px-3 py-1.5 text-sm ' +
                (dias === opcion
                  ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                  : 'border-[var(--color-border-subtle)] hover:border-[var(--color-accent)]')
              }
            >
              {t('paginas.dias', { n: opcion })}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {[
          { titulo: t('paginas.visitas'), valor: total.visitas, pie: t('paginas.abiertas') },
          {
            titulo: t('paginas.visitantes'),
            valor: total.visitantes,
            pie: t('paginas.distintos'),
          },
          {
            titulo: t('paginas.clics'),
            valor: total.clics,
            pie: total.visitas > 0 ? `${Math.round((total.clics / total.visitas) * 100)}%` : '—',
          },
        ].map((dato) => (
          <Card key={dato.titulo}>
            <p className="text-xs text-[var(--color-ink-muted)]">{dato.titulo}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">
              {dato.valor.toLocaleString(idiomaActual())}
            </p>
            <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">{dato.pie}</p>
          </Card>
        ))}
      </div>

      {total.visitas === 0 ? (
        <EmptyState
          title={t('paginas.nadieEntro')}
          description={t(
            datos.porInfluencer.length === 0
              ? 'paginas.ningunaPublicada'
              : 'paginas.nadieLasAbrio',
          )}
        />
      ) : (
        <>
          <Card title={t('paginas.porDia')}>
            {/*
              Barras y no un gráfico de líneas: son pocos días y lo que se compara es el tamaño de
              cada uno, no una tendencia continua. Una línea sobre siete puntos sugiere una
              suavidad que los datos no tienen.
            */}
            <ul className="mt-1 space-y-1.5">
              {datos.porDia.map((dia) => (
                <li key={dia.dayUtc} className="flex items-center gap-3 text-xs">
                  <span className="w-16 shrink-0 text-[var(--color-ink-muted)]">
                    {dia.dayUtc.slice(5)}
                  </span>
                  <span className="h-4 flex-1 overflow-hidden rounded bg-[var(--color-surface-sunken)]">
                    <span
                      className="block h-full rounded bg-[var(--color-accent)]"
                      style={{ width: `${Math.max(2, (dia.visitas / pico) * 100)}%` }}
                    />
                  </span>
                  <span className="w-28 shrink-0 text-right tabular-nums">
                    {t('paginas.filaDia', {
                      visitas: dia.visitas,
                      personas: dia.visitantes,
                      clics: dia.clics,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          {/*
            El experimento, arriba de todo lo demas.

            Lo que se compara es la TASA y no los clics: una rama con mas clics puede haberse
            mostrado mas —el reparto es parejo con volumen, pero no exacto— y quedarse con el
            numero absoluto haria ganar a la que tuvo mas trafico.

            Las filas con variante «-» son de antes del experimento y se muestran igual: son
            trafico real, y esconderlas haria que los totales de arriba no cierren con esta tabla.
          */}
          {datos.porVariante.length > 0 && (
            <Card title={t('paginas.experimento')}>
              <p className="mb-3 text-sm text-[var(--color-ink-muted)]">
                {t('paginas.experimentoQue')}
              </p>
              <ul className="divide-y divide-[var(--color-border-subtle)]">
                {datos.porVariante.map((fila) => {
                  const tasa = fila.visitas > 0 ? (fila.clics / fila.visitas) * 100 : null;
                  const mejor = Math.max(
                    ...datos.porVariante.map((otra) =>
                      otra.visitas > 0 ? (otra.clics / otra.visitas) * 100 : 0,
                    ),
                  );
                  return (
                    <li
                      key={fila.variante}
                      className="flex flex-wrap items-center gap-3 py-2.5 text-sm"
                    >
                      <span className="w-40 shrink-0 font-medium">
                        {t(`paginas.rama${fila.variante}` as 'paginas.ramaA') ?? fila.variante}
                      </span>
                      <span className="flex-1 text-xs text-[var(--color-ink-muted)] tabular-nums">
                        {t('paginas.ramaCuenta', {
                          visitas: fila.visitas,
                          clics: fila.clics,
                        })}
                      </span>
                      <span
                        className={
                          'shrink-0 text-sm font-semibold tabular-nums ' +
                          (tasa !== null && tasa >= mejor && mejor > 0
                            ? 'text-[var(--color-accent)]'
                            : '')
                        }
                      >
                        {tasa === null ? '—' : `${tasa.toFixed(1)}%`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}

          {/*
            La comparación entre influencers no entra en la ficha de una.

            Es la tabla que las pone lado a lado, y dentro de una ficha compite con sus propios
            cortes: se leerían las dos como si hablaran de lo mismo. Vive en la pantalla general,
            que es donde comparar tiene sentido.
          */}
          {!creatorFijo && (
          <Card title={t('paginas.porInfluencer')}>
            <ul className="divide-y divide-[var(--color-border-subtle)]">
              {datos.porInfluencer.map((fila) => (
                /*
                  La elegida queda marcada, porque esta tabla NO se filtra.

                  Es a propósito —es la comparación y la lista del selector— pero entonces con una
                  influencer elegida la pantalla muestra dos cosas a la vez: sus cortes arriba y
                  todas las filas acá. Sin marcar cuál es, las dos se leen como si hablaran de lo
                  mismo.
                */
                <li
                  key={fila.creatorId}
                  className={
                    'flex flex-wrap items-center gap-3 py-2.5 ' +
                    (fila.creatorId === datos.creatorId
                      ? '-mx-2 rounded-lg bg-[var(--color-accent-soft)] px-2'
                      : '')
                  }
                >
                  <Link
                    to={`/influencer/${fila.creatorId}`}
                    className="min-w-0 flex-1 truncate text-sm font-medium hover:text-[var(--color-accent)]"
                  >
                    {fila.publicName}
                  </Link>
                  <a
                    href={`/p/${fila.slug}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="truncate text-xs text-[var(--color-ink-muted)] underline underline-offset-2"
                  >
                    /p/{fila.slug}
                  </a>
                  <span className="shrink-0 text-xs tabular-nums">
                    {t('paginas.filaInfluencer', {
                      visitas: fila.visitas,
                      personas: fila.visitantes,
                      clics: fila.clics,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
          )}

          <div className="grid items-start gap-4 lg:grid-cols-3">
            <Card title={t('paginas.deDondeVienen')}>
              <Lista
                filas={datos.porOrigen.map((fila) => ({
                  nombre: fila.origen === 'directo' ? t('paginas.directo') : fila.origen,
                  valor: fila.visitas,
                }))}
                vacio={t('paginas.sinDatos')}
              />
            </Card>
            <Card title={t('paginas.desdeQuePais')}>
              <Lista
                filas={datos.porPais.map((fila) => ({
                  nombre: fila.pais === 'XX' ? t('paginas.sinDeterminar') : fila.pais,
                  valor: fila.visitas,
                }))}
                vacio={t('paginas.sinDatos')}
              />
            </Card>
            <Card title={t('paginas.adondeVan')}>
              <Lista
                filas={datos.porDestino.map((fila) => ({
                  nombre: NOMBRE_DE_DESTINO[fila.destino] ?? fila.destino,
                  valor: fila.clics,
                }))}
                vacio={t('paginas.nadieSiguio')}
              />
            </Card>
          </div>
        </>
      )}

      {/*
        Qué se guarda, dicho en la pantalla que lo muestra.

        Es donde alguien se pregunta de dónde salen estos números, y es el único momento en que
        va a leerlo. Enterrado en una política que nadie abre, la respuesta existe y no se conoce.
      */}
      <p className="text-xs text-[var(--color-ink-muted)]">
        {t('paginas.queSeGuarda')}
      </p>
    </div>
  );
}

function Lista({
  filas,
  vacio,
}: {
  filas: { nombre: string; valor: number }[];
  vacio: string;
}) {
  if (filas.length === 0) {
    return <p className="text-sm text-[var(--color-ink-muted)]">{vacio}</p>;
  }

  return (
    <ul className="space-y-1.5">
      {filas.map((fila) => (
        <li key={fila.nombre} className="flex items-center justify-between gap-3 text-sm">
          <span className="min-w-0 truncate">{fila.nombre}</span>
          <span className="shrink-0 tabular-nums">{fila.valor.toLocaleString('es')}</span>
        </li>
      ))}
    </ul>
  );
}
